import { useEffect, useState } from "react";

import type { PlayerListItem, SeasonModel } from "../../shared/types/contracts";
import { Badge } from "../../shared/ui/badge";
import { Button } from "../../shared/ui/button";
import { ConfirmationDialog } from "../../shared/ui/confirmation-dialog";
import { Field } from "../../shared/ui/field";
import { Input } from "../../shared/ui/input";
import { Spinner } from "../../shared/ui/spinner";
import { InlineSaveInput } from "../../shared/ui/inline-save-input";
import { actionKeys } from "../../hooks/actionKeys";
import type { IntentStatusView } from "../../hooks/useIntentStatus";
import { EMPTY_INTENT_STATUS } from "../../hooks/useIntentStatus";
import type { RevisionedCommandResult } from "../../hooks/teamTypes";

export type ManagementConfirmation =
  "discard" | "newInvite" | "revokeInvite" | null;

export interface ManagementInvite {
  loading: boolean;
  token: string | null;
  active: boolean;
}

export interface ManagementPresence {
  id: string;
  state: "online" | "offline";
  claimedPlayerNameSnapshot: string | null;
}

export interface ManagementActivity {
  id: string;
  occurredLabel: string;
  actorNameSnapshot: string;
  summary: string;
}

interface TeamManagementPanelProps {
  googleEmail: string | null;
  linkBusy: boolean;
  claimedPlayerId: string | null;
  hasActiveGame?: boolean;
  seasons?: SeasonModel[];
  activeSeasonId?: string | null;
  serverBusy?: boolean;
  actionStatus?: IntentStatusView;
  players: PlayerListItem[];
  confirmUnclaim: boolean;
  unclaimBusy: boolean;
  teamName: string;
  teamNameRevision: number;
  newPlayerName: string;
  confirmation: ManagementConfirmation;
  invite: ManagementInvite;
  inviteUrl: string | null;
  inviteCopied: boolean;
  presence: ManagementPresence[];
  currentUserId: string | null;
  activityLoading: boolean;
  activity: ManagementActivity[];
  onLinkAccount: () => void;
  onRequestUnclaim: () => void;
  onUnclaim: () => void;
  onCancelUnclaim: () => void;
  onSetTeamName: (teamName: string) => Promise<RevisionedCommandResult>;
  onNewPlayerNameChange: (name: string) => void;
  onAddPlayer: () => Promise<void>;
  onConfirm: (confirmation: ManagementConfirmation) => void;
  onDiscardGame: () => void;
  onStartSeason?: (year: number) => Promise<boolean>;
  onCloseSeason?: () => Promise<boolean>;
  onReopenSeason?: (seasonId: string) => Promise<boolean>;
  onCopyInvite: () => void;
  onRotateInvite: () => void;
  onRevokeInvite: () => void;
}

function ManagementHeading({ children }: { children: string }) {
  return (
    <h3 className="m-0 font-mono text-xs font-bold tracking-[0.12em] text-warning uppercase">
      {children}
    </h3>
  );
}

export function TeamManagementPanel({
  googleEmail,
  linkBusy,
  claimedPlayerId,
  hasActiveGame = true,
  seasons = [],
  activeSeasonId = null,
  actionStatus = EMPTY_INTENT_STATUS,
  players,
  confirmUnclaim,
  unclaimBusy,
  teamName,
  teamNameRevision,
  newPlayerName,
  confirmation,
  invite,
  inviteUrl,
  inviteCopied,
  presence,
  currentUserId,
  activityLoading,
  activity,
  onLinkAccount,
  onRequestUnclaim,
  onUnclaim,
  onCancelUnclaim,
  onSetTeamName,
  onNewPlayerNameChange,
  onAddPlayer,
  onConfirm,
  onDiscardGame,
  onStartSeason = async () => false,
  onCloseSeason = async () => false,
  onReopenSeason = async () => false,
  onCopyInvite,
  onRotateInvite,
  onRevokeInvite,
}: TeamManagementPanelProps) {
  const claimedName =
    players.find((player) => player.id === claimedPlayerId)?.name ||
    "your player";
  const onlinePresence = presence.filter((entry) => entry.state === "online");
  const latestYear = seasons.reduce(
    (latest, season) => Math.max(latest, season.year ?? 0),
    0,
  );
  const suggestedYear = Math.max(new Date().getUTCFullYear(), latestYear + 1);
  const [newYear, setNewYear] = useState(suggestedYear);
  const [confirmClose, setConfirmClose] = useState(false);
  const activeSeason = seasons.find((season) => season.id === activeSeasonId);
  const latestClosedSeason = !activeSeasonId
    ? seasons.find(
        (season) => season.year === latestYear && season.status === "closed",
      )
    : null;
  const addPlayerPending = actionStatus.isPending(actionKeys.addPlayer);
  const startSeasonPending = actionStatus.isPending(actionKeys.seasonStart);
  const closeSeasonPending = actionStatus.isPending(actionKeys.seasonClose);
  const seasonPending = actionStatus.countFor(actionKeys.seasonGroup) > 0;
  const discardPending = actionStatus.isPending(actionKeys.discardGame);
  const rotatePending = actionStatus.isPending(actionKeys.inviteRotate);
  const revokePending = actionStatus.isPending(actionKeys.inviteRevoke);
  const teamNameUnknown =
    actionStatus.phaseFor(actionKeys.teamName) === "outcome_unknown";
  const addPlayerUnknown =
    actionStatus.phaseFor(actionKeys.addPlayer) === "outcome_unknown";
  const startSeasonUnknown =
    actionStatus.phaseFor(actionKeys.seasonStart) === "outcome_unknown";
  const closeSeasonUnknown =
    actionStatus.phaseFor(actionKeys.seasonClose) === "outcome_unknown";
  const discardUnknown =
    actionStatus.phaseFor(actionKeys.discardGame) === "outcome_unknown";
  const rotateUnknown =
    actionStatus.phaseFor(actionKeys.inviteRotate) === "outcome_unknown";
  const revokeUnknown =
    actionStatus.phaseFor(actionKeys.inviteRevoke) === "outcome_unknown";

  useEffect(() => {
    setNewYear((current) => (current <= latestYear ? suggestedYear : current));
  }, [latestYear, suggestedYear]);

  return (
    <div className="grid gap-4">
      <section className="grid gap-2">
        <ManagementHeading>ACCOUNT</ManagementHeading>
        {googleEmail ? (
          <div className="truncate rounded-[var(--radius-sm)] border border-success/40 bg-success/10 px-3 py-2 text-sm text-success">
            🔗 Google linked — {googleEmail}
          </div>
        ) : (
          <>
            <p className="m-0 text-xs leading-relaxed text-foreground-muted">
              Signed in anonymously. Link Google to keep this player claim when
              switching devices.
            </p>
            <Button
              type="button"
              onClick={onLinkAccount}
              pending={linkBusy}
              pendingLabel="LINKING…"
              variant="outline"
              className="w-full"
            >
              🔗 LINK GOOGLE ACCOUNT
            </Button>
          </>
        )}
        {claimedPlayerId ? (
          <Button
            type="button"
            onClick={onRequestUnclaim}
            size="sm"
            variant="ghost"
            className="w-full border border-dashed border-warning/60 text-warning"
          >
            UNCLAIM PLAYER — {claimedName}
          </Button>
        ) : null}
      </section>

      <section className="grid gap-2 border-t border-border pt-4">
        <ManagementHeading>TEAM NAME</ManagementHeading>
        <InlineSaveInput
          value={teamName}
          authoritativeRevision={teamNameRevision}
          aria-label="Team name"
          onSave={onSetTeamName}
          unknown={teamNameUnknown}
        />
      </section>

      <section className="grid gap-2 border-t border-border pt-4">
        <ManagementHeading>SEASON</ManagementHeading>
        {activeSeason ? (
          <>
            <p className="m-0 text-xs leading-relaxed text-foreground-muted">
              {activeSeason.label} is active for the regular season and
              playoffs.
            </p>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="w-full border border-dashed border-danger/70 text-danger hover:text-danger"
              disabled={seasonPending}
              onClick={() => setConfirmClose(true)}
            >
              CLOSE {activeSeason.label} SEASON
            </Button>
          </>
        ) : (
          <>
            {latestClosedSeason ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                pending={actionStatus.isPending(
                  actionKeys.seasonReopen(latestClosedSeason.id),
                )}
                pendingLabel={
                  actionStatus.phaseFor(
                    actionKeys.seasonReopen(latestClosedSeason.id),
                  ) === "outcome_unknown"
                    ? "CHECKING…"
                    : "REOPENING…"
                }
                disabled={seasonPending}
                onClick={() => void onReopenSeason(latestClosedSeason.id)}
              >
                REOPEN {latestClosedSeason.label}
              </Button>
            ) : null}
            <label className="grid gap-1 text-sm font-bold">
              NEW SEASON YEAR
              <Input
                aria-label="New season year"
                type="number"
                min={1900}
                max={2200}
                value={newYear}
                disabled={seasonPending}
                onChange={(event) => setNewYear(Number(event.target.value))}
              />
            </label>
            <Button
              type="button"
              size="sm"
              pending={startSeasonPending}
              pendingLabel={startSeasonUnknown ? "CHECKING…" : "STARTING…"}
              disabled={
                !Number.isInteger(newYear) ||
                newYear < 1900 ||
                newYear > 2200 ||
                newYear <= latestYear ||
                seasonPending
              }
              onClick={() => void onStartSeason(newYear)}
            >
              START SEASON
            </Button>
          </>
        )}
      </section>

      <section className="grid gap-2">
        <ManagementHeading>ADD PLAYER</ManagementHeading>
        <Field className="flex grid-cols-[minmax(0,1fr)_auto] flex-row gap-2">
          <Input
            value={newPlayerName}
            disabled={addPlayerPending}
            onChange={(event) => onNewPlayerNameChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") void onAddPlayer();
            }}
            placeholder="Player name"
          />
          <Button
            type="button"
            onClick={() => void onAddPlayer()}
            pending={addPlayerPending}
            pendingLabel={addPlayerUnknown ? "CHECKING…" : "ADDING…"}
            disabled={!newPlayerName.trim()}
          >
            ADD
          </Button>
        </Field>
      </section>

      {hasActiveGame ? (
        <section className="border-t border-border pt-4">
          <Button
            type="button"
            onClick={() => onConfirm("discard")}
            size="sm"
            variant="ghost"
            className="w-full border border-dashed border-danger/70 text-danger hover:text-danger"
          >
            DISCARD GAME IN PROGRESS
          </Button>
        </section>
      ) : null}

      <section className="grid gap-2 border-t border-border pt-4">
        <ManagementHeading>INVITE TEAMMATES</ManagementHeading>
        {invite.loading ? (
          <div className="flex items-center gap-2 py-2 text-xs text-foreground-muted">
            <Spinner />
            Loading invite…
          </div>
        ) : inviteUrl && invite.active ? (
          <>
            <div className="flex gap-2">
              <Input
                readOnly
                value={inviteUrl}
                aria-label="Team invite link"
                onFocus={(event) => event.target.select()}
                className="font-mono text-xs"
              />
              <Button
                type="button"
                onClick={onCopyInvite}
                size="sm"
                variant={inviteCopied ? "selected" : "outline"}
                className="h-[var(--control-input)]"
              >
                {inviteCopied ? "COPIED ✓" : "📋 COPY"}
              </Button>
            </div>
            <p className="m-0 text-xs leading-relaxed text-foreground-muted">
              Anyone holding this fixed link can join. Make a new link to
              invalidate the old one, or revoke joining entirely.
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                onClick={() => onConfirm("newInvite")}
                size="sm"
                variant="outline"
                className="flex-1"
              >
                ↻ MAKE NEW LINK
              </Button>
              <Button
                type="button"
                onClick={() => onConfirm("revokeInvite")}
                size="sm"
                variant="ghost"
                className="text-danger hover:text-danger"
              >
                REVOKE
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="m-0 text-xs leading-relaxed text-foreground-muted">
              {invite.active && !invite.token
                ? "This team's link predates link display — make a new one to get a fixed, copyable link. The old link stops working then."
                : "The invite is off right now — make a new link to let people join again."}
            </p>
            <Button
              type="button"
              onClick={() => onConfirm("newInvite")}
              size="sm"
              variant="outline"
              className="w-full"
            >
              ↻ MAKE NEW LINK
            </Button>
          </>
        )}
      </section>

      <section className="grid gap-2 border-t border-border pt-4">
        <div className="flex items-center justify-between gap-2">
          <ManagementHeading>ONLINE NOW</ManagementHeading>
          <Badge variant={onlinePresence.length ? "success" : "neutral"}>
            {onlinePresence.length}
          </Badge>
        </div>
        {onlinePresence.length === 0 ? (
          <p className="m-0 text-xs text-foreground-muted">No one is online.</p>
        ) : (
          <div className="grid gap-1">
            {onlinePresence.map((entry) => (
              <div key={entry.id} className="flex items-center gap-2 text-sm">
                <span
                  aria-hidden="true"
                  className="size-2 rounded-full bg-success"
                />
                <span className="truncate">
                  {entry.claimedPlayerNameSnapshot || "Invited member"}
                  {entry.id === currentUserId ? (
                    <span className="text-foreground-muted"> (you)</span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="grid gap-2 border-t border-border pt-4">
        <ManagementHeading>ACTIVITY</ManagementHeading>
        {activityLoading ? (
          <div className="flex items-center gap-2 py-2 text-xs text-foreground-muted">
            <Spinner />
            Loading…
          </div>
        ) : activity.length === 0 ? (
          <p className="m-0 text-xs text-foreground-muted">Nothing yet.</p>
        ) : (
          <div className="grid">
            {activity.map((entry) => (
              <div
                key={entry.id}
                className="border-t border-border/60 py-2 text-xs first:border-0 first:pt-0"
              >
                <span className="mr-1 font-mono text-[0.65rem] text-foreground-muted">
                  {entry.occurredLabel}
                </span>
                <strong>{entry.actorNameSnapshot}</strong> {entry.summary}
              </div>
            ))}
          </div>
        )}
      </section>

      <ConfirmationDialog
        open={confirmClose}
        title={`Close the ${activeSeason?.label ?? "active"} season?`}
        description="All playable scheduled games must be completed or removed first. The roster and members carry forward, while the next season starts with an empty schedule and stats."
        confirmLabel="CLOSE SEASON"
        pending={closeSeasonPending}
        pendingLabel="CLOSING…"
        unknown={closeSeasonUnknown}
        onRetryUnknown={actionStatus.retryUnknown}
        onOpenChange={setConfirmClose}
        onConfirm={async () => {
          if (await onCloseSeason()) setConfirmClose(false);
        }}
      />
      <ConfirmationDialog
        open={confirmUnclaim}
        title={`Unclaim ${claimedName}?`}
        description="You will return to player selection. This does not transfer the claim or team membership to another account."
        confirmLabel="UNCLAIM"
        pending={unclaimBusy}
        pendingLabel="UNCLAIMING…"
        onConfirm={onUnclaim}
        onOpenChange={(open) => {
          if (!open) onCancelUnclaim();
        }}
      />
      <ConfirmationDialog
        open={!confirmUnclaim && confirmation === "discard"}
        title="Discard the game in progress?"
        description="Score, statistics, and the play log will be lost. Nothing is archived."
        confirmLabel="YES, DISCARD"
        pending={discardPending}
        pendingLabel="DISCARDING…"
        unknown={discardUnknown}
        onRetryUnknown={actionStatus.retryUnknown}
        onConfirm={onDiscardGame}
        onOpenChange={(open) => {
          if (!open) onConfirm(null);
        }}
      />
      <ConfirmationDialog
        open={!confirmUnclaim && confirmation === "newInvite"}
        title="Make a new invite link?"
        description="The current link stops working immediately."
        confirmLabel="SURE? OLD LINK DIES"
        pending={rotatePending}
        pendingLabel="MAKING LINK…"
        unknown={rotateUnknown}
        onRetryUnknown={actionStatus.retryUnknown}
        onConfirm={onRotateInvite}
        onOpenChange={(open) => {
          if (!open) onConfirm(null);
        }}
      />
      <ConfirmationDialog
        open={!confirmUnclaim && confirmation === "revokeInvite"}
        title="Turn off team joining?"
        description="The current invite link will stop working until a new one is created."
        confirmLabel="SURE?"
        pending={revokePending}
        pendingLabel="REVOKING…"
        unknown={revokeUnknown}
        onRetryUnknown={actionStatus.retryUnknown}
        onConfirm={onRevokeInvite}
        onOpenChange={(open) => {
          if (!open) onConfirm(null);
        }}
      />
    </div>
  );
}
